import React from 'react';
import { AxiosError } from 'axios';
import { Column, Options } from 'material-table';
import { Button, createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';

import { KeywordInclusionContext } from '../KeywordInclusionContext';
import { HomeContext } from '../../Home/HomeContext';
import { getTableColumn } from '../../../shared/operators';
import { SearchStatus } from '../../../shared/enum/SearchStatus';
import { TableEmpty } from '../../../shared/components/TableEmpty';
import { TableCommon } from '../../../shared/components/TableCommon';
import { TableLoading } from '../../../shared/components/TableLoading';
import { theme } from '../../../theme';
import StorageKeywordSearchService from '../../../Services/StorageKeywordSearchService';
import { CategoryKeywordSearchModel } from '../../../Models/CategoryKeywordSearchModel';
import { StorageKeywordInsertModel, storageKeywordInsertModelInitial } from '../../../Models/StorageKeywordInsertModel';
import { clientModelInitial } from '../../../Models/ClientModel';

const useStyles = makeStyles((theme: Theme) => createStyles({
    includeButton: {
        marginTop: theme.spacing(3)
    }
}));

export function KeywordInclusionSearchResult(){
    const classes = useStyles();

    const keywordInclusionContext = React.useContext(KeywordInclusionContext);
    const homeContext = React.useContext(HomeContext);

    const [categoryKeywordSearchResult, setCategoryKeywordSearchResult] = React.useState<CategoryKeywordSearchModel[]>([]);

    React.useEffect(() => {
        const categoryKeywordSearchResult = JSON.parse(JSON.stringify(keywordInclusionContext.categoryKeywordSearchResult));
        setCategoryKeywordSearchResult(categoryKeywordSearchResult);
    }, [keywordInclusionContext.categoryKeywordSearchResult]);

    const categorizationSearchColumns: Column<CategoryKeywordSearchModel>[] = [
        {
            title: 'Título da publicação',
            field: 'title',
            type: 'string',
            align: 'left',
            cellStyle: { minWidth: '200px' },
            render: rowData => getTitleLink(rowData)
        },
        getTableColumn('ID da classificação', 'classificationId', 'numeric', 'left', { maxWidth: '50px' }),
        getTableColumn('ID da categoria', 'categoryIndex', 'numeric', 'left', { maxWidth: '50px' }),
        getTableColumn('Veículo', 'vehicleNomenclature', 'string', 'left', { maxWidth: '200px' }),
        getTableColumn('Palavras-chave', 'keywords', 'string', 'left', { minWidth: '200px' })
    ];

    const tableOptions: Options<CategoryKeywordSearchModel> = {
        filtering: true,
        toolbar: false,
        showTitle: false,
        pageSizeOptions: [10, 50, 100, 200],
        pageSize: 10,
        selection: true,
        showSelectAllCheckbox: true,
        selectionProps: () => ({
            color: 'primary'
        }),
        rowStyle: (rowData: CategoryKeywordSearchModel) => ({
            color: getRowColor(rowData)
        }),
    };
    
    const handleInclude = () => {
        const [ids, hasInactiveCategory] = handleTableSelection();

        if (!hasInactiveCategory){
            if (ids.length == 0)
                homeContext.showWarning('Ops, nenhuma categorização foi selecionada.');
            else {
                const storageKeywordInsert = {...storageKeywordInsertModelInitial};
                storageKeywordInsert.categoriesId = ids;

                storageKeywordInsert.keyword = keywordInclusionContext.selectedKeyword;
        
                if (storageKeywordInsert.keyword.description != '')
                    insertKeyword(storageKeywordInsert);
                else
                    homeContext.showWarning('Ops, nenhuma palavra-chave foi selecionada.');
            }
        }
        else {
            homeContext.showWarning('Ops, você selecionou uma categorização que já foi removida, ' + 
                'verifique a seleção de categorizações e tente novamente.');
        }
    };

    const insertKeyword = (storageKeywordInsert: StorageKeywordInsertModel) => { 
        
        homeContext.setOpenLoadingDialog(true);
        
        storageKeywordInsert.client = JSON.parse(JSON.stringify(keywordInclusionContext.selectedClient));

        StorageKeywordSearchService
            .storageKeywordSearchService(storageKeywordInsert.client.id)
            .create(storageKeywordInsert)
            .then(() => {
                showInsertionSuccessModal(storageKeywordInsert.keyword.description, storageKeywordInsert.categoriesId.length);
                keywordInclusionContext.setSelectedClient(clientModelInitial);
                keywordInclusionContext.setCategoryKeywordSearchResult([]);
                keywordInclusionContext.setSelectedInitialDate(null);
                keywordInclusionContext.setSelectedFinalDate(null);
                keywordInclusionContext.setClientError(false);
                keywordInclusionContext.setInitialDateError(false);
                keywordInclusionContext.setFinalDateError(false);
                keywordInclusionContext.setSearchStatus(SearchStatus.INITIAL);
            })
            .catch((error: AxiosError) => homeContext.showError('', error))
            .finally(() => homeContext.setOpenLoadingDialog(false));
    };

    const handleTableSelection = () => {
        const list = JSON.parse(JSON.stringify(categoryKeywordSearchResult));
        let hasInactiveCategory = false;
        const ids = list.reduce((result: number[], item: any) => {   
            if (item.tableData && item.tableData.checked){
                if (item.categoryActive)
                    result.push(item.categoryId);
                else
                    hasInactiveCategory = true;
            }    
            return result;
        }, []);

        return [ids, hasInactiveCategory];
    };

    const showInsertionSuccessModal = (keywordDescription: string, quantityInserted: number) => {
        homeContext.showSuccess(`Palavra-chave ${keywordDescription} foi adicionada em ${quantityInserted} categorizações.`);
    };

    const getRowColor = (rowData: CategoryKeywordSearchModel): string => {
        if (!rowData.categoryActive)
            return theme.palette.text.disabled;
        else
            return theme.palette.text.primary;
    };

    const getTitleLink = (rowData: CategoryKeywordSearchModel) => {
        if (rowData.categoryActive)
            return (<a href={rowData.clipperLink} target='_blank' rel="noreferrer noopener">{rowData.title}</a>);
        else
            return rowData.title;
    };
    
    const getLoadingTable = () => {
        return (
            <Grid item xs={12}>
                <TableLoading displayText='Buscando categorizações...' />
            </Grid>
        );
    };

    const getResultTable = () => {
        return (
            <>
                <Grid item xs={12}>
                    <Typography variant="body1">
                        {categoryKeywordSearchResult.length} {
                            categoryKeywordSearchResult.length > 1 
                                ? 'categorizações encontradas'
                                : 'categorização encontrada'
                        }
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <TableCommon
                        tableData={categoryKeywordSearchResult}
                        tableColumns={categorizationSearchColumns}
                        emptyDataSourceMessage='Nenhuma categorização encontrada'
                        extraOptions={tableOptions} />
                </Grid>
                <Grid
                    container
                    direction="row"
                    justify="flex-end"
                    alignItems="center"
                    spacing={2}
                    className={classes.includeButton}>
                    <Grid item>
                        <Button
                            onClick={handleInclude}
                            variant='contained'
                            color='primary'
                            size='large'>
                                INCLUIR PALAVRA CHAVE
                        </Button>
                    </Grid>
                </Grid>
            </>
        );
    };

    const getEmptyTable = () => {
        return (
            <Grid
                item
                xs={12}>
                <TableEmpty displayText='Nenhuma categorização encontrada' />
            </Grid>
        );
    };

    return (
        <>
            {keywordInclusionContext.searchStatus == SearchStatus.LOADING && getLoadingTable()}
            {keywordInclusionContext.searchStatus == SearchStatus.RESULT && getResultTable()}
            {keywordInclusionContext.searchStatus == SearchStatus.EMPTY && getEmptyTable()}
        </>
    );
}